import * as React from 'react';
import { cn } from '@/lib/utils';
import { hasFieldChrome } from '@/lib/fieldChrome';
import { Label } from '@/components/ui/label';
import styles from './textarea.module.css';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: React.ReactNode;
  helperText?: React.ReactNode;
  label?: React.ReactNode;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    allProps,
    ref,
  ) => {
    const {
      'aria-describedby': ariaDescribedby,
      'aria-invalid': ariaInvalid,
      className,
      error,
      helperText,
      id,
      label,
      required,
      enterKeyHint = 'enter',
      onKeyDown,
      placeholder,
      readOnly,
      ...props
    } = allProps;
    const generatedId = React.useId();
    const textareaId = id ?? generatedId;
    const helperTextId = helperText ? `${textareaId}-helper` : undefined;
    const errorId = error ? `${textareaId}-error` : undefined;
    const describedBy = [ariaDescribedby, helperTextId, errorId].filter(Boolean).join(' ') || undefined;

    const isFieldLayout = hasFieldChrome(allProps, label, helperText);

    const control = (
      <textarea
        id={textareaId}
        ref={ref}
        required={required}
        aria-describedby={describedBy}
        aria-invalid={ariaInvalid ?? (error ? true : undefined)}
        {...props}
        readOnly={readOnly}
        // A placeholder in a locked field reads like a value; show only real values.
        placeholder={readOnly ? undefined : placeholder}
        enterKeyHint={enterKeyHint}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.stopPropagation();
          onKeyDown?.(e);
        }}
        className={cn(styles.root, 'text-body-m', className)}
      />
    );

    if (!isFieldLayout) {
      return control;
    }

    return (
      <div className={styles.field}>
        {label && (
          <Label htmlFor={textareaId}>
            {label}
            {required && <span aria-hidden="true" className={styles.required}>*</span>}
          </Label>
        )}
        {control}
        {helperText && (
          <p id={helperTextId} className={styles.helper}>
            {helperText}
          </p>
        )}
        {error && (
          <p id={errorId} role="alert" className={styles.error}>
            {error}
          </p>
        )}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';

export { Textarea };
